/**
 * Facility Group register without an assignment mode (AVA-602).
 *
 * A Group register that already has rows but no Shared/Separated mode cannot be read by a
 * member Facility: the API (ExceptionMiddleware) answers every dashboard widget read - saved-widget
 * query, preview, grid, columns and values - and the register grid's member `table-data` read with
 * HTTP 400 `{ code, message }` using the code below, and flags the same register on the dashboard
 * `/tables` source list with `configurationRequired: true`.
 *
 * This module is the single place the UI recognises that contract (dashboards and the register
 * grid), so each surface shows the same calm "not configured" state instead of a generic failure.
 * Only the typed `code` is matched - never the message text.
 *
 * No `@/` alias imports: this file is exercised directly by `node --test`.
 */

export const REGISTER_CONFIGURATION_REQUIRED_CODE = 'REGISTER_ASSIGNMENT_MODE_CONFIGURATION_REQUIRED';

export const REGISTER_NOT_CONFIGURED_COPY = Object.freeze({
  title: 'Register not configured',
  message: 'This Facility Group register has rows but no Shared or Separated mode yet. An administrator must configure it.',
  chip: 'Not configured',
  // Short form for the data-source picker row, where the full sentence would crowd the list.
  pickerHint: 'Needs a Shared or Separated mode. An administrator must configure it.',
});

const HTTP_BAD_REQUEST = 400;

function readErrorBody(data) {
  if (data && typeof data === 'object') return data;
  if (typeof data !== 'string' || !data) return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_parseErr) {
    return null;
  }
}

/**
 * True when an axios error is the API's "register needs an assignment mode" rejection.
 * @param {unknown} err axios error (or anything a promise rejected with)
 * @returns {boolean}
 */
export function isRegisterConfigurationRequiredError(err) {
  const response = err && err.response;
  if (!response || response.status !== HTTP_BAD_REQUEST) return false;
  const body = readErrorBody(response.data);
  return !!body && body.code === REGISTER_CONFIGURATION_REQUIRED_CODE;
}

/**
 * True when a `/tables` source entry is a Group register that cannot be read until configured.
 * @param {{ configurationRequired?: boolean } | null | undefined} source
 * @returns {boolean}
 */
export function isConfigurationRequiredSource(source) {
  return !!source && source.configurationRequired === true;
}
